import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchDecalSignals, triggerRefresh, type RefreshResult } from "../lib/api";
import type { DecalSignal, DecalDomain, SignalStage } from "../lib/types";
import DomainPill from "../components/ui/DomainPill";
import SignalTypeBadge from "../components/ui/SignalTypeBadge";
import { cn } from "../lib/cn";

// ─── Local filter state ───────────────────────────────────────────────────────

type ScorePreset = 0 | 60 | 75 | 90;

interface Filters {
  domain:    DecalDomain | "";
  min_score: ScorePreset;
  stage:     SignalStage | "";
  search:    string;
}

const DEFAULT_FILTERS: Filters = { domain: "", min_score: 60, stage: "", search: "" };

// ─── Options ──────────────────────────────────────────────────────────────────

const DOMAIN_OPTIONS: { value: DecalDomain | ""; label: string }[] = [
  { value: "",                            label: "All domains" },
  { value: "LICENSING_COMPLIANCE",        label: "Licensing & Compliance" },
  { value: "HEALTH_SAFETY",              label: "Health & Safety" },
  { value: "PREK_PROGRAMS",              label: "Pre-K Programs" },
  { value: "WORKFORCE",                  label: "Workforce" },
  { value: "QUALITY_INITIATIVES",        label: "Quality Ratings" },
  { value: "FAMILY_ACCESS_AFFORDABILITY", label: "Family Access" },
  { value: "DATA_PRIVACY_REPORTING",     label: "Data & Privacy" },
  { value: "TECHNOLOGY_AI_EDUCATION",    label: "Tech & AI" },
  { value: "LOTTERY_HOPE_FUNDING",       label: "Lottery / HOPE" },
  { value: "CAMPUS_ADJACENT",            label: "Campus Facilities" },
];

const SCORE_PRESETS: { value: ScorePreset; label: string }[] = [
  { value: 0,  label: "All" },
  { value: 60, label: "60+" },
  { value: 75, label: "75+" },
  { value: 90, label: "90+" },
];

const STAGE_OPTIONS: { value: SignalStage | ""; label: string }[] = [
  { value: "",           label: "All stages" },
  { value: "introduced", label: "Introduced" },
  { value: "advancing",  label: "Advancing" },
  { value: "enacted",    label: "Enacted" },
];

// ─── Risk styling ─────────────────────────────────────────────────────────────

const RISK_STRIP: Record<string, string> = {
  High:   "bg-risk-high",
  Medium: "bg-risk-medium",
  Low:    "bg-risk-low",
};

const RISK_SCORE_COLOR: Record<string, string> = {
  High:   "text-risk-high",
  Medium: "text-risk-medium",
  Low:    "text-risk-low",
};

const RISK_SCORE_BG: Record<string, string> = {
  High:   "bg-red-50",
  Medium: "bg-amber-50",
  Low:    "bg-teal-50",
};

// ─── SegmentedControl ─────────────────────────────────────────────────────────

interface SegOption<T> { value: T; label: string }

function SegmentedControl<T extends string | number>({
  options, value, onChange, ariaLabel,
}: {
  options:   SegOption<T>[];
  value:     T;
  onChange:  (v: T) => void;
  ariaLabel: string;
}) {
  return (
    <div role="group" aria-label={ariaLabel}
      className="flex gap-px rounded-lg border border-border bg-surface-sunken p-0.5"
    >
      {options.map((opt) => (
        <button
          key={String(opt.value)}
          type="button"
          onClick={() => onChange(opt.value)}
          aria-pressed={value === opt.value}
          className={cn(
            "rounded-[5px] px-3 py-1 text-sm font-medium transition-all duration-150",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
            value === opt.value
              ? "bg-surface-raised text-ink shadow-sm"
              : "text-ink-secondary hover:text-ink"
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── FilterBar ────────────────────────────────────────────────────────────────

function FilterBar({ filters, onChange }: { filters: Filters; onChange: (f: Filters) => void }) {
  const isDefault =
    filters.domain === "" && filters.min_score === 60 && filters.stage === "" && filters.search === "";

  return (
    <div
      className="sticky top-16 z-40 border-b border-border-subtle bg-surface/95 backdrop-blur-sm"
      role="search"
      aria-label="Signal filters"
    >
      <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-2 px-6 py-2.5">

        {/* Bill number / title search */}
        <div className="relative">
          <svg className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-tertiary"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 111 11a6 6 0 0116 0z" />
          </svg>
          <input
            type="search"
            placeholder="Bill number or keyword…"
            value={filters.search}
            onChange={(e) => onChange({ ...filters, search: e.target.value })}
            aria-label="Search by bill number or title"
            className={cn(
              "w-52 rounded-full border py-1.5 pl-8 pr-3.5 text-sm transition-colors",
              "bg-surface-raised focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-light",
              filters.search !== ""
                ? "border-primary font-medium text-ink"
                : "border-border text-ink-secondary placeholder:text-ink-disabled"
            )}
          />
        </div>

        <div className="h-5 w-px bg-border-subtle" aria-hidden />

        {/* Domain */}
        <div className="relative">
          <select
            id="domain-filter"
            aria-label="Filter by domain"
            value={filters.domain}
            onChange={(e) => onChange({ ...filters, domain: e.target.value as DecalDomain | "" })}
            className={cn(
              "min-w-[172px] cursor-pointer appearance-none rounded-full border py-1.5 pl-3.5 pr-8",
              "bg-surface-raised text-sm transition-colors",
              "focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-light",
              filters.domain !== ""
                ? "border-primary font-medium text-primary"
                : "border-border text-ink-secondary"
            )}
          >
            {DOMAIN_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <svg className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-tertiary"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        <div className="h-5 w-px bg-border-subtle" aria-hidden />

        {/* Score */}
        <SegmentedControl
          options={SCORE_PRESETS}
          value={filters.min_score}
          onChange={(v) => onChange({ ...filters, min_score: v as ScorePreset })}
          ariaLabel="Minimum impact score"
        />

        <div className="h-5 w-px bg-border-subtle" aria-hidden />

        {/* Stage */}
        <SegmentedControl
          options={STAGE_OPTIONS}
          value={filters.stage}
          onChange={(v) => onChange({ ...filters, stage: v as SignalStage | "" })}
          ariaLabel="Legislative stage"
        />

        {/* Reset — only when non-default */}
        {!isDefault && (
          <>
            <div className="h-5 w-px bg-border-subtle" aria-hidden />
            <button
              onClick={() => onChange(DEFAULT_FILTERS)}
              className="flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium text-ink-tertiary transition-colors hover:bg-surface-sunken hover:text-ink"
            >
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
              Reset
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Refresh button ───────────────────────────────────────────────────────────

function RefreshButton({ onRefreshed }: { onRefreshed: () => void }) {
  const [state, setState] = useState<"idle" | "loading" | "success" | "quota" | "error">("idle");

  async function handleClick() {
    setState("loading");
    try {
      const result: RefreshResult = await triggerRefresh();
      if (!result.ok && result.quotaExceeded) {
        setState("quota");
      } else if (!result.ok) {
        setState("error");
      } else {
        setState("success");
        // Reload signals after a short delay to let the pipeline start
        setTimeout(onRefreshed, 3000);
      }
    } catch {
      setState("error");
    }
  }

  if (state === "quota") {
    return (
      <div className="mt-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3.5">
        <svg className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
        <div>
          <p className="text-sm font-semibold text-amber-800">Monthly data limit reached</p>
          <p className="mt-0.5 text-sm text-amber-700">
            The LegiScan API quota for this month has been exceeded. Data will refresh automatically on the 1st.
            To refresh sooner, please reach out to your admin to upgrade to a paid subscription.
          </p>
          <button onClick={() => setState("idle")} className="mt-2 text-xs font-medium text-amber-700 underline underline-offset-2 hover:text-amber-900">
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  if (state === "success") {
    return (
      <div className="mt-4 flex items-center gap-2.5 rounded-2xl border border-teal-200 bg-teal-50 px-4 py-3">
        <svg className="h-4 w-4 shrink-0 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-sm font-medium text-teal-800">
          Refresh started — new signals will appear in approximately 5 minutes.
        </p>
      </div>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={state === "loading"}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors",
        "border-border-subtle bg-surface text-ink-secondary hover:border-primary/30 hover:text-primary",
        state === "loading" && "cursor-not-allowed opacity-50"
      )}
      aria-label="Refresh legislative data"
    >
      <svg
        className={cn("h-3.5 w-3.5", state === "loading" && "animate-spin")}
        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden
      >
        <path strokeLinecap="round" strokeLinejoin="round"
          d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
      </svg>
      {state === "loading" ? "Starting…" : "Refresh data"}
    </button>
  );
}

// ─── Hero summary bar ─────────────────────────────────────────────────────────

function HeroBar({ total, signals, onRefresh }: { total: number; signals: DecalSignal[]; onRefresh: () => void }) {
  const highPriority = signals.filter((s) => s.impact_score >= 75).length;
  const latest = signals[0]
    ? new Date(signals[0].created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : null;

  return (
    <div className="mx-auto max-w-5xl px-6 pt-8 pb-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Today's Signals</h1>
          <p className="mt-1 text-sm text-ink-secondary">
            High-impact Georgia bills affecting early care and learning — sorted by impact score.
          </p>
        </div>
        <RefreshButton onRefreshed={onRefresh} />
      </div>

      {total > 0 && (
        <div className="mt-5 flex flex-wrap gap-6 border-t border-border-subtle pt-5">
          <Stat label="Active signals" value={String(total)} />
          <div className="w-px bg-border-subtle" />
          <Stat label="High priority" value={String(highPriority)} highlight={highPriority > 0} />
          {latest && (
            <>
              <div className="w-px bg-border-subtle" />
              <Stat label="Latest signal" value={latest} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className={cn("text-2xl font-bold tabular-nums leading-none", highlight ? "text-risk-high" : "text-ink")}>
        {value}
      </span>
      <span className="text-xs text-ink-tertiary">{label}</span>
    </div>
  );
}

// ─── SignalCard ───────────────────────────────────────────────────────────────

function SignalCard({ signal }: { signal: DecalSignal }) {
  const navigate = useNavigate();
  const dest = `/bills/${signal.bill_id}`;

  const signalDate = new Date(signal.created_at).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
  const showUrgency = signal.urgency !== signal.risk_level;

  return (
    <article
      onClick={() => navigate(dest, { state: { from: "/" } })}
      className={cn(
        "group cursor-pointer overflow-hidden rounded-2xl border border-border-subtle bg-surface-raised",
        "shadow-sm transition-all duration-200",
        "hover:-translate-y-px hover:shadow-md hover:border-border"
      )}
    >
      {/* Top risk strip — full width, clearly communicates severity */}
      <div className={cn("h-1.5 w-full", RISK_STRIP[signal.risk_level] ?? "bg-border")} aria-hidden />

      <div className="px-6 py-5">

        {/* Header: signal badge + score cluster */}
        <div className="mb-4 flex items-start justify-between gap-4">
          <SignalTypeBadge type={signal.signal_type} size="sm" />

          {/* Score pill */}
          <div className={cn(
            "flex shrink-0 flex-col items-center rounded-xl px-3.5 py-2",
            RISK_SCORE_BG[signal.risk_level] ?? "bg-surface-sunken"
          )}>
            <span className={cn(
              "text-2xl font-bold tabular-nums leading-none",
              RISK_SCORE_COLOR[signal.risk_level] ?? "text-ink"
            )}
              aria-label={`Impact score ${signal.impact_score}`}
            >
              {signal.impact_score}
            </span>
            <span className="mt-0.5 text-2xs font-semibold uppercase tracking-wider text-ink-tertiary">
              {signal.risk_level} risk
              {showUrgency && ` · ${signal.urgency} urgency`}
            </span>
          </div>
        </div>

        {/* Bill metadata */}
        <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
          <span className="rounded-md bg-surface-sunken px-2 py-0.5 font-mono text-xs font-medium text-ink-secondary">
            {signal.state} · {signal.bill_number}
          </span>
          <span className="text-xs text-ink-tertiary">{signal.status_label}</span>
        </div>

        {/* Title */}
        <h2 className="mb-2 text-base font-semibold leading-snug text-ink transition-colors duration-150 group-hover:text-primary">
          {signal.title}
        </h2>

        {/* Short message */}
        <p className="mb-4 text-sm leading-relaxed text-ink-secondary">
          {signal.short_message}
        </p>

        {/* Domain chips */}
        {signal.decal_domains.length > 0 && (
          <div className="mb-4 flex flex-wrap gap-1.5">
            {signal.decal_domains.slice(0, 2).map((d) => (
              <DomainPill key={d} domain={d as DecalDomain} size="sm" />
            ))}
            {signal.decal_domains.length > 2 && (
              <span className="inline-flex items-center rounded bg-surface-sunken px-2 py-0.5 text-2xs font-medium text-ink-tertiary">
                +{signal.decal_domains.length - 2} more
              </span>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between gap-4 border-t border-border-subtle pt-3.5">
          <div className="min-w-0">
            {signal.last_action ? (
              <p className="truncate text-xs text-ink-tertiary">
                <span className="font-medium text-ink-secondary">Last action: </span>
                {signal.last_action}
              </p>
            ) : (
              <p className="text-xs text-ink-disabled">No recent action on record</p>
            )}
            <p className="mt-0.5 text-2xs text-ink-disabled">Signal raised {signalDate}</p>
          </div>

          {/* Text CTA with animated arrow */}
          <span
            className="flex shrink-0 items-center gap-1 text-xs font-medium text-primary opacity-0 transition-all duration-150 group-hover:opacity-100 group-hover:translate-x-0.5"
            aria-hidden
          >
            View details
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </span>
        </div>

      </div>
    </article>
  );
}

// ─── Skeleton card ────────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="overflow-hidden rounded-2xl border border-border-subtle bg-surface-raised shadow-sm">
      <div className="h-1.5 w-full animate-pulse bg-surface-sunken" />
      <div className="animate-pulse px-6 py-5 space-y-3.5">
        <div className="flex items-start justify-between">
          <div className="h-5 w-28 rounded-full bg-surface-sunken" />
          <div className="h-12 w-14 rounded-xl bg-surface-sunken" />
        </div>
        <div className="h-3 w-48 rounded bg-surface-sunken" />
        <div className="space-y-1.5">
          <div className="h-4 w-full rounded bg-surface-sunken" />
          <div className="h-4 w-3/4 rounded bg-surface-sunken" />
        </div>
        <div className="space-y-1.5">
          <div className="h-3.5 w-full rounded bg-surface-sunken" />
          <div className="h-3.5 w-5/6 rounded bg-surface-sunken" />
        </div>
        <div className="flex gap-2">
          <div className="h-5 w-24 rounded bg-surface-sunken" />
          <div className="h-5 w-20 rounded bg-surface-sunken" />
        </div>
        <div className="flex items-center justify-between border-t border-border-subtle pt-3.5">
          <div className="space-y-1">
            <div className="h-3 w-52 rounded bg-surface-sunken" />
            <div className="h-2.5 w-32 rounded bg-surface-sunken" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState({ filtersActive }: { filtersActive: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-28 text-center">
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-surface-sunken">
        <svg className="h-7 w-7 text-ink-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      </div>
      <p className="text-base font-semibold text-ink-secondary">No signals to show</p>
      <p className="mt-1.5 max-w-xs text-sm leading-relaxed text-ink-tertiary">
        {filtersActive
          ? "No signals match your filters. Try lowering the minimum score or clearing the domain filter."
          : "The pipeline hasn't detected any high-impact activity yet. Check back after the next scheduled run."}
      </p>
    </div>
  );
}

// ─── Error state ──────────────────────────────────────────────────────────────

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-28 text-center">
      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
        <svg className="h-7 w-7 text-risk-high" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
      </div>
      <p className="text-base font-semibold text-ink">Couldn't load signals</p>
      <p className="mt-1.5 max-w-xs text-sm leading-relaxed text-ink-secondary">
        There was a problem reaching the server. Your filters are saved — just try again.
      </p>
      <button
        onClick={onRetry}
        className="mt-6 rounded-full bg-primary px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        Try again
      </button>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function TodaysSignals() {
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [signals, setSignals] = useState<DecalSignal[]>([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(async (f: Filters) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchDecalSignals({
        domain:    f.domain    || undefined,
        min_score: f.min_score || undefined,
        stage:     f.stage     || undefined,
        limit:     100,
        page:      1,
      });
      setSignals(res.data);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Only re-fetch from API when server-side filters change (not search — that's client-side)
  useEffect(() => { void load(filters); }, [filters.domain, filters.min_score, filters.stage, load]);

  const q = filters.search.trim().toLowerCase();
  const visibleSignals = q
    ? signals.filter(
        (s) =>
          s.bill_number.toLowerCase().includes(q) ||
          s.title.toLowerCase().includes(q)
      )
    : signals;

  const filtersActive =
    filters.domain !== "" || filters.min_score !== 0 || filters.stage !== "" || filters.search !== "";

  return (
    // Fade-in on mount
    <div className="animate-fade-in">

      {/* Hero — always above the sticky bar */}
      {!loading && !error && (
        <HeroBar total={total} signals={signals} onRefresh={() => load(filters)} />
      )}
      {loading && (
        <div className="mx-auto max-w-5xl px-6 pt-8 pb-6">
          <div className="h-7 w-48 animate-pulse rounded bg-surface-sunken" />
          <div className="mt-2 h-4 w-80 animate-pulse rounded bg-surface-sunken" />
        </div>
      )}

      <FilterBar filters={filters} onChange={setFilters} />

      <div className="mx-auto max-w-5xl px-6 py-8">

        {/* Loading */}
        {loading && (
          <div className="space-y-4" aria-live="polite" aria-label="Loading signals">
            {Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        )}

        {/* Error */}
        {!loading && error && <ErrorState onRetry={() => load(filters)} />}

        {/* Empty */}
        {!loading && !error && visibleSignals.length === 0 && (
          <EmptyState filtersActive={filtersActive} />
        )}

        {/* Results */}
        {!loading && !error && visibleSignals.length > 0 && (
          <div aria-live="polite">
            <p className="mb-5 text-xs text-ink-tertiary">
              {visibleSignals.length.toLocaleString()} {visibleSignals.length === 1 ? "signal" : "signals"}
              {filtersActive && " matching your filters"}
            </p>

            <div className="space-y-4">
              {visibleSignals.map((s) => <SignalCard key={s.id} signal={s} />)}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
